import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import {
  Brand,
  ClinicalNote,
  OrganisationCache,
  Patient,
  Referral,
  ReferralClinicalNote,
  ReferralInteraction,
  ReferralMedia,
  ReferralMessage,
  ReferralNote,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  IClinicalNote,
  IContact,
  IContactNumber,
  IPatient,
  IPatientContactDetails,
  IPractice,
  IReferral,
  IReferralClinicalNote,
  IReferralInteraction,
  IReferralMedia,
  IReferralMessage,
  IReferralNote,
  IReferralPatientLetter,
  IStaffer,
  OptionalPatientDetails,
  PatientRelationshipType,
  PrimaryContact,
  ReferralActor,
  ReferralParticipant,
  ReferralPatientDetails,
  isStaffer,
  isWithPrimaryContact,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  INamedDocument,
  WithRef,
  doc,
  isReffable,
  isSameRef,
  multiSort,
  snapshot,
  sortByCreatedAt,
  sortMoment,
  toMoment,
} from '@principle-theorem/shared';
import { isEqual } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { ReferralService } from '../services/referral.service';

interface ILoadReferralData {
  brand: WithRef<IBrand>;
  id: string;
  referral?: WithRef<IReferral>;
}

export interface IReferralState {
  referral?: WithRef<IReferral>;
  patient?: WithRef<IPatient>;
  primaryContact?: PrimaryContact;
  contactNumbers?: IContactNumber[];
  staffer?: WithRef<IStaffer>;
  referrer?: ReferralParticipant;
  recipient?: ReferralParticipant;
  practice?: WithRef<IPractice>;
  changes?: Partial<IReferral>;
  messages: WithRef<IReferralMessage>[];
  interactions: WithRef<IReferralInteraction>[];
  media: WithRef<IReferralMedia>[];
  clinicalNotes: WithRef<IReferralClinicalNote>[];
  patientClinicalNotes: WithRef<IClinicalNote>[];
  notes: WithRef<IReferralNote>[];
  currentUser?: ReferralActor;
  loading: boolean;
  referralUnavailable: boolean;
  error?: string;
}

const initialState: IReferralState = {
  referral: undefined,
  patient: undefined,
  primaryContact: undefined,
  contactNumbers: [],
  staffer: undefined,
  referrer: undefined,
  practice: undefined,
  recipient: undefined,
  changes: undefined,
  messages: [],
  interactions: [],
  media: [],
  clinicalNotes: [],
  patientClinicalNotes: [],
  notes: [],
  currentUser: undefined,
  loading: false,
  referralUnavailable: false,
  error: undefined,
};

@Injectable()
export class ReferralStore extends ComponentStore<IReferralState> {
  private _referralService = inject(ReferralService);

  readonly referral$ = this.select((state) => state.referral);
  readonly patient$ = this.select((state) => state.patient);
  readonly primaryContact$ = this.select((state) => state.primaryContact);
  readonly contactNumbers$ = this.select((state) => state.contactNumbers);
  readonly staffer$ = this.select((state) => state.staffer);
  readonly referrer$ = this.select((state) => state.referrer);
  readonly recipient$ = this.select((state) => state.recipient);
  readonly practice$ = this.select((state) => state.practice);
  readonly changes$ = this.select((state) => state.changes);
  readonly loading$ = this.select((state) => state.loading);
  readonly referralUnavailable$ = this.select(
    (state) => state.referralUnavailable
  );
  readonly messages$ = this.select((state) => state.messages);
  readonly interactions$ = this.select((state) => state.interactions);
  readonly media$ = this.select((state) => state.media);
  readonly clinicalNotes$ = this.select((state) => state.clinicalNotes);
  readonly patientClinicalNotes$ = this.select(
    (state) => state.patientClinicalNotes
  );
  readonly notes$ = this.select((state) => state.notes);
  readonly currentUser$ = this.select((state) => state.currentUser);
  readonly patientLetter$ = this.select(
    this.referral$,
    this.changes$,
    (referral, changes) => {
      if (changes?.patientLetter) {
        return changes.patientLetter;
      }
      return referral?.patientLetter;
    }
  );

  readonly hasChanges$ = this.select(
    this.referral$,
    this.changes$,
    (referral, changes) => {
      if (!referral || !changes) {
        return false;
      }

      return Object.keys(changes).some((key) => {
        const original = referral[key as keyof IReferral];
        const changed = changes[key as keyof IReferral];

        if (isReffable(original) && isReffable(changed)) {
          return !isSameRef(
            original as INamedDocument | DocumentReference,
            changed as INamedDocument | DocumentReference
          );
        }
        return !isEqual(original, changed);
      });
    }
  );

  // UPDATERS
  readonly setReferral = this.updater(
    (state, referral: WithRef<IReferral>) => ({
      ...state,
      referral,
    })
  );

  readonly markReferralUnavailable = this.updater((state) => ({
    ...state,
    referral: undefined,
    loading: false,
    referralUnavailable: true,
  }));

  readonly setPatient = this.updater((state, patient: WithRef<IPatient>) => ({
    ...state,
    patient,
  }));

  readonly setReferrer = this.updater(
    (state, referrer: ReferralParticipant) => ({
      ...state,
      referrer,
    })
  );

  readonly setPractice = this.updater(
    (state, practice: WithRef<IPractice>) => ({
      ...state,
      practice,
    })
  );

  readonly setRecipient = this.updater(
    (state, recipient: ReferralParticipant | undefined) => ({
      ...state,
      recipient,
    })
  );

  readonly setStaffer = this.updater((state, staffer: WithRef<IStaffer>) => ({
    ...state,
    staffer,
  }));

  readonly setPatientPrimaryContact = this.updater(
    (state, primaryContact: PrimaryContact | undefined) => ({
      ...state,
      primaryContact,
    })
  );

  readonly setPatientContactNumbers = this.updater(
    (state, contactNumbers: IContactNumber[] | undefined) => ({
      ...state,
      contactNumbers,
    })
  );

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    error: loading ? undefined : state.error,
  }));

  readonly setChanges = this.updater(
    (state, changes: Partial<IReferral> | undefined) => {
      const updatedChanges = changes
        ? { ...state.changes, ...changes }
        : undefined;

      return {
        ...state,
        changes: updatedChanges,
      };
    }
  );

  readonly setMessages = this.updater(
    (state, messages: WithRef<IReferralMessage>[]) => ({
      ...state,
      messages,
    })
  );

  readonly setMedia = this.updater(
    (state, media: WithRef<IReferralMedia>[]) => ({
      ...state,
      media,
    })
  );

  readonly setInteractions = this.updater(
    (state, interactions: WithRef<IReferralInteraction>[]) => ({
      ...state,
      interactions,
    })
  );

  readonly setClinicalNotes = this.updater(
    (state, clinicalNotes: WithRef<IReferralClinicalNote>[]) => ({
      ...state,
      clinicalNotes,
    })
  );

  readonly setPatientClinicalNotes = this.updater(
    (state, patientClinicalNotes: WithRef<IClinicalNote>[]) => ({
      ...state,
      patientClinicalNotes,
    })
  );

  readonly setNotes = this.updater(
    (state, notes: WithRef<IReferralNote>[]) => ({
      ...state,
      notes,
    })
  );

  readonly updatePatientLetter = this.updater(
    (state, patientLetter: Partial<IReferralPatientLetter>) => {
      const currentLetter =
        state.changes?.patientLetter ?? state.referral?.patientLetter;
      const updatedLetter = {
        ...currentLetter,
        ...patientLetter,
      } as IReferralPatientLetter;

      return {
        ...state,
        changes: {
          ...state.changes,
          patientLetter: updatedLetter,
        },
      };
    }
  );

  readonly setCurrentUser = this.updater(
    (state, currentUser: ReferralActor | undefined) => ({
      ...state,
      currentUser,
    })
  );

  readonly reset = this.updater(() => initialState);

  // EFFECTS
  readonly loadReferral = this.effect((data$: Observable<ILoadReferralData>) =>
    data$.pipe(
      switchMap(({ brand, id, referral }) =>
        referral
          ? of(referral)
          : Firestore.doc$(doc(Brand.referralCol(brand), id))
      ),
      tapResponse(
        (selectedReferral) => this.setReferral(selectedReferral),
        // eslint-disable-next-line no-console
        console.error
      )
    )
  );

  readonly loadReferralForPortal = this.effect(
    (data$: Observable<{ uid: string; email: string }>) =>
      data$.pipe(
        tap(() => this.setLoading(true)),
        switchMap(({ uid, email }) => Referral.byContact$(uid, email)),
        tapResponse(
          (matchingReferrals) => this._applyPortalLookup(matchingReferrals),
          (error) => {
            // eslint-disable-next-line no-console
            console.error(error);
            this.markReferralUnavailable();
          }
        ),
        tap(() => this.setLoading(false))
      )
  );

  readonly loadPatient = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) =>
          OrganisationCache.patients.getDoc(referral.patient.ref)
        ),
        tapResponse(
          (patient) => this.setPatient(patient),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadPrimaryContact = this.effect(
    (patient$: Observable<WithRef<IPatient> | undefined>) =>
      patient$.pipe(
        map((patient) =>
          isWithPrimaryContact(patient) ? patient.primaryContact : undefined
        ),
        tapResponse(
          (primaryContact) => this.setPatientPrimaryContact(primaryContact),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadPatientContactNumbers = this.effect(
    (patient$: Observable<WithRef<IPatient>>) =>
      patient$.pipe(
        switchMap((patient) => Patient.resolveContactNumbers(patient)),
        tapResponse(
          (contactNumbers) => this.setPatientContactNumbers(contactNumbers),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadReferrer = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) =>
          Firestore.getDoc<IStaffer | IContact>(referral.referrer.ref)
        ),
        tapResponse(
          (referrer) => {
            if (isStaffer(referrer)) {
              this.setStaffer(referrer);
            }
            this.setReferrer(referrer as ReferralParticipant);
          },
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadRecipient = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) =>
          Firestore.getDoc<IStaffer | IContact>(referral.recipient.ref)
        ),
        tapResponse(
          (recipient) => {
            if (isStaffer(recipient)) {
              this.setStaffer(recipient);
            }
            this.setRecipient(recipient as ReferralParticipant);
          },
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadPractice = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) =>
          OrganisationCache.practices.getDoc(referral.practice.ref)
        ),
        tapResponse(
          (practice) => this.setPractice(practice),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadMessages = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) => ReferralMessage.all$(referral)),
        map((messages) => messages.sort(sortByCreatedAt).reverse()),
        tapResponse(
          (messages) => this.setMessages(messages),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadInteractions = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) => ReferralInteraction.all$(referral)),
        map((interactions) => interactions.sort(sortByCreatedAt).reverse()),
        tapResponse(
          (interactions) => this.setInteractions(interactions),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadMedia = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) =>
          ReferralMedia.all$(referral).pipe(
            map((media) => media.sort(sortByCreatedAt).reverse()),
            tapResponse(
              (media) => this.setMedia(media),
              // eslint-disable-next-line no-console
              console.error
            )
          )
        )
      )
  );

  readonly loadClinicalNotes = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) => ReferralClinicalNote.all$(referral)),
        map((clinicalNotes) => clinicalNotes.sort(sortByCreatedAt).reverse()),
        tapResponse(
          (clinicalNotes) => this.setClinicalNotes(clinicalNotes),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadPatientClinicalNotes = this.effect(
    (patient$: Observable<WithRef<IPatient>>) =>
      patient$.pipe(
        switchMap((patient) =>
          Patient.withPatientRelationships$(
            patient,
            [PatientRelationshipType.DuplicatePatient],
            ClinicalNote.all$
          ).pipe(
            switchMap((notes) =>
              ClinicalNote.filterEmpty$(notes).pipe(
                multiSort((noteA, noteB) =>
                  sortMoment(
                    toMoment(noteA.recordDate),
                    toMoment(noteB.recordDate)
                  )
                )
              )
            )
          )
        ),
        tapResponse(
          (patientClinicalNotes) =>
            this.setPatientClinicalNotes(patientClinicalNotes),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly loadNotes = this.effect(
    (referral$: Observable<WithRef<IReferral>>) =>
      referral$.pipe(
        switchMap((referral) => ReferralNote.all$(referral)),
        map((notes) => notes.sort(sortByCreatedAt).reverse()),
        tapResponse(
          (notes) => this.setNotes(notes),
          // eslint-disable-next-line no-console
          console.error
        )
      )
  );

  readonly setupAutoSave = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() =>
        combineLatest([
          this.referral$.pipe(filter((referral) => !!referral)),
          this.hasChanges$,
        ]).pipe(
          debounceTime(1000),
          filter(([_, hasChanges]) => hasChanges),
          switchMap(async ([referral, _]) => {
            const changes = await snapshot(this.changes$);
            const currentUser = await snapshot(this.currentUser$);

            if (!changes || !currentUser) {
              return;
            }

            try {
              const updatedReferral = await this._referralService.autoSave(
                referral,
                changes,
                currentUser
              );
              if (updatedReferral) {
                this.setReferral(updatedReferral);
                this.setChanges(undefined);
              }
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Auto-save failed:', error);
            }
          })
        )
      )
    )
  );

  readonly updateOptionalPatientDetails = this.effect(
    (options$: Observable<OptionalPatientDetails>) =>
      options$.pipe(
        tapResponse(
          (options) => {
            const patient = this.get().patient;
            const contactNumbers = this.get().contactNumbers;

            if (!patient) {
              // eslint-disable-next-line no-console
              console.error('No patient available to update details.');
              return;
            }

            const updatedDetails = Referral.applyPatientDetailsOptions(
              patient,
              options,
              contactNumbers
            );

            this.updatePatientDetails(of(updatedDetails));
          },
          (error) =>
            // eslint-disable-next-line no-console
            console.error('Error updating optional patient details:', error)
        )
      )
  );

  readonly updatePatientDetails = this.effect(
    (details$: Observable<Partial<IPatientContactDetails>>) =>
      details$.pipe(
        distinctUntilChanged(isEqual),
        tapResponse(
          (details) => {
            const currentDetails = this._getCurrentPatientDetails();

            this.setChanges({
              patientDetails: {
                ...currentDetails,
                ...(details || {}),
              } as ReferralPatientDetails,
            });
          },
          (error) =>
            // eslint-disable-next-line no-console
            console.error('Error updating patient details:', error)
        )
      )
  );

  constructor() {
    super(initialState);
  }

  private _getCurrentPatientDetails(): ReferralPatientDetails | undefined {
    const referral = this.get().referral;
    const changes = this.get().changes;

    if (!referral) {
      return undefined;
    }

    return {
      ...referral.patientDetails,
      ...changes?.patientDetails,
    } as ReferralPatientDetails;
  }

  private _applyPortalLookup(matchingReferrals: WithRef<IReferral>[]): void {
    const [referral] = matchingReferrals;
    if (!referral) {
      this.markReferralUnavailable();
      return;
    }
    this.patchState({ referral, referralUnavailable: false });
  }
}
